<script>
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/src/sweetalert2.scss";

export default {
  name: "CompanyBrandingForm",
  props: { id: { type: String, default: null } },
  data() {
    return {
      loading: false,
      f: {
        company_name: "",
        slug: "", // kept for edit mode
        primary_color: "",
        secondary_color: "",
        login_heading: "",
        login_description: "",
        is_active: true,
        phone: "",
        address: "",
        vimeo_id: "",
      },
      files: { logo: null, background: null },
      preview: { logo: "", background: "" },
      baseUrl:
        document.querySelector('meta[name="app-url"]')?.content ||
        window.location.origin,
    };
  },
  created() {
    if (this.id) this.loadById(this.id);
  },
  methods: {
    normalizeSlug() {
      if (!this.id) return; // no slug handling on create
      if (this.f.slug == null) return;
      this.f.slug = String(this.f.slug)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },
    onFile(e, key) {
      const file = e.target.files?.[0];
      if (!file) return;
      this.files[key] = file;
      this.preview[key] = URL.createObjectURL(file);
    },

    // --- color helpers ---
    ensurePickerHex(v, fallback) {
      if (!v) return fallback;
      let s = String(v).trim().toLowerCase();
      if (!s.startsWith('#')) s = `#${s}`;
      let hex = s.slice(1);
      if (hex.length === 3 && /^[0-9a-f]{3}$/i.test(hex)) {
        // expand #abc => #aabbcc
        s = '#' + hex.split('').map(c => c + c).join('');
      } else if (hex.length >= 6 && /^[0-9a-f]{6,8}$/i.test(hex)) {
        // keep first 6 (ignore alpha if present)
        s = '#' + hex.slice(0, 6);
      } else {
        s = fallback;
      }
      return s;
    },
    normalizeColor(key) {
      const fallback = key === 'primary_color' ? '#003056' : '#cf2e2e';
      this.f[key] = this.ensurePickerHex(this.f[key], fallback);
    },
    // ----------------------

    async loadById(id) {
      this.loading = true;
      try {
        try {
          const { data } = await this.$http.get(`branding/${id}`);
          if (data && data.id) {
            this.f = this.mapFromApi(data);
            return;
          }
        } catch (_) {}

        const first = await this.$http.get("branding", { params: { page: 1 } });
        const meta = first.data?.meta || { last_page: 1 };
        const findIn = (list) => (list || []).find(r => String(r.id) === String(id));
        let row = findIn(first.data?.data);
        let page = 2;
        while (!row && page <= meta.last_page) {
          const { data } = await this.$http.get("branding", { params: { page } });
          row = findIn(data?.data);
          page++;
        }
        if (!row) {
          Swal.fire("Not found", "Record no longer exists.", "warning");
          this.$router.back();
          return;
        }
        this.f = this.mapFromApi(row);
      } catch (e) {
        Swal.fire("Error", "Failed to load record.", "error");
      } finally {
        this.loading = false;
      }
    },

    mapFromApi(row) {
      return {
        company_name: row.company_name || "",
        slug: row.slug || "",
        primary_color: this.ensurePickerHex(row.primary_color, "#003056"),
        secondary_color: this.ensurePickerHex(row.secondary_color, "#cf2e2e"),
        login_heading: row.login_heading || "",
        login_description: row.login_description || "",
        is_active: !!row.is_active,
        phone: row.phone || "",
        address: row.address || "",
        vimeo_id: row.vimeo_id || "",
      };
    },

    async submit() {
      this.loading = true;
      try {
        const payload = { ...this.f };

        // normalize colors before sending (ensures #rrggbb)
        payload.primary_color = this.ensurePickerHex(payload.primary_color, "#003056");
        payload.secondary_color = this.ensurePickerHex(payload.secondary_color, "#cf2e2e");

        if (!this.id) {
          // do not send slug on create; backend will generate from company_name
          delete payload.slug;
        }

        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ""));
        if (this.files.logo) fd.append("logo", this.files.logo);
        if (this.files.background) fd.append("background", this.files.background);

        if (this.id) {
          await this.$http.post(`branding/${this.id}`, fd);
          Swal.fire("Success", "Company updated.", "success");
        } else {
          await this.$http.post("branding", fd);
          Swal.fire("Success", "Company created.", "success");
        }
        this.$router.push({ name: "BrandingIndex" });
      } catch (e) {
        let msg =
          e?.response?.data?.message ||
          "Validation error or unexpected failure. Please check inputs.";
        if (e?.response?.data?.errors) {
          const firstKey = Object.keys(e.response.data.errors)[0];
          if (firstKey && e.response.data.errors[firstKey]?.[0]) {
            msg = e.response.data.errors[firstKey][0];
          }
        }
        Swal.fire("Error", msg, "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<template>
  <div class="content" v-loading.fullscreen.lock="loading">
    <base-header class="pb-6">
      <div class="row align-items-center py-2"></div>
    </base-header>

    <div class="container-fluid mt--6">
      <card>
        <h2 slot="header" class="mb-0"> {{ id ? "Edit Company" : "Create Company" }} Brand Settings</h2>

        <form @submit.prevent="submit" enctype="multipart/form-data">
          <div class="form-row">
            <div class="col-md-6">
              <label class="form-control-label">Company Name <span class="requireField">*</span></label>
              <base-input
                name="Company Name"
                placeholder="Company Name"
                v-model="f.company_name"
              />
            </div>

            <!-- Slug ONLY visible when editing -->
            <div class="col-md-6" v-if="id">
              <label class="form-control-label">Slug <span class="requireField">*</span></label>
              <base-input name="Slug" placeholder="slug" v-model="f.slug" @input="normalizeSlug" />
              <small class="text-muted">
                Link: {{ baseUrl }}/#/company/{{ f.slug || "your-slug" }}
              </small>
            </div>

            <!-- Color pickers -->
            <div class="col-md-6">
              <label class="form-control-label d-block">Primary Color</label>
              <input
                type="color"
                class="form-control form-control-color"
                v-model="f.primary_color"
                @change="normalizeColor('primary_color')"
                title="Choose primary color"
              />
              <small class="text-muted">{{ f.primary_color }}</small>
            </div>

            <div class="col-md-6">
              <label class="form-control-label d-block">Secondary Color</label>
              <input
                type="color"
                class="form-control form-control-color"
                v-model="f.secondary_color"
                @change="normalizeColor('secondary_color')"
                title="Choose secondary color"
              />
              <small class="text-muted">{{ f.secondary_color }}</small>
            </div>

            <div class="col-md-6">
              <label class="form-control-label">Phone Number</label>
              <base-input name="Phone Number" placeholder="+1 555 555 5555" v-model="f.phone" />
            </div>

            <div class="col-md-6">
              <label class="form-control-label">Address</label>
              <base-input name="Address" placeholder="123 Main St, City, ST 00000" v-model="f.address" />
            </div>

            <div class="col-md-6">
              <label class="form-control-label">Login Heading</label>
              <base-input name="Login Heading" placeholder="Welcome to …" v-model="f.login_heading" />
            </div>

            <div class="col-md-6">
              <label class="form-control-label">Vimeo Video ID</label>
              <base-input name="Vimeo Id" placeholder="123456789" v-model="f.vimeo_id" />
            </div>

            <div class="col-md-12">
              <label class="form-control-label">Login Description</label>
              <textarea class="form-control" rows="6" v-model="f.login_description"
                        placeholder="Landing page welcome copy…"></textarea>
            </div>

            <div class="col-md-6 mt-3">
              <label class="form-control-label">Logo</label>
              <input type="file" class="form-control" accept="image/*" @change="onFile($event,'logo')" />
              <div v-if="preview.logo" class="mt-2">
                <img :src="preview.logo" alt="logo" style="height:48px;object-fit:contain" />
              </div>
            </div>

            <div class="col-md-6 mt-3">
              <label class="form-control-label">Background Image</label>
              <input type="file" class="form-control" accept="image/*" @change="onFile($event,'background')" />
              <div v-if="preview.background" class="mt-2">
                <img :src="preview.background" alt="bg" style="height:48px;object-fit:cover" />
              </div>
            </div>

            <div class="col-md-12 mt-3">
              <label class="form-check">
                <input type="checkbox" class="form-check-input" v-model="f.is_active" />
                <span class="form-check-label">Active</span>
              </label>
            </div>
          </div>

          <div class="text-left mt-3">
            <base-button class="btn greybtn" type="danger" @click="$router.go(-1)">Cancel</base-button>
            <base-button class="btn btn-primary" type="primary" native-type="submit">
              {{ id ? "Update" : "Submit" }}
            </base-button>
          </div>
        </form>
      </card>
    </div>
  </div>
</template>

<style scoped></style>
